if (!customElements.get('product-popup-connector')) {
  /**
   * A custom element that displays a product popup on hover over images.
   *
   * Displays product information in a floating card when hovering over lookbook images.
   * Shows product title, price (with compare-at price if available), and a customizable link.
   *
   * The popup automatically finds and attaches to sibling image blocks within the same group.
   * On desktop, the popup appears on hover with configurable positioning.
   * On mobile, the popup appears centered on tap and dismisses on outside click or scroll.
   */
  class ProductPopupConnector extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.popup = this.querySelector('[data-popup-card]');
    /** @type {HTMLElement | null} */
    this.imageBlock = null;
    /** @type {number} */
    this.offsetX = parseFloat(this.dataset.offsetX) || 50;
    /** @type {number} */
    this.offsetY = parseFloat(this.dataset.offsetY) || 50;
    /** @type {number} */
    this.offsetXMobile = parseFloat(this.dataset.offsetXMobile) || 50;
    /** @type {number} */
    this.offsetYMobile = parseFloat(this.dataset.offsetYMobile) || 50;
    /** @type {number} */
    this.dimAmount = parseFloat(this.dataset.dimAmount) || 10;
    /** @type {boolean} */
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;
    /** @type {boolean} */
    this.isActive = false;
    /** @type {boolean} */
    this.isHoveringPopup = false;
    /** @type {number | null} */
    this.hideTimeout = null;
    /** @type {number | null} */
    this.hideAnimationTimeout = null;
  }

  connectedCallback() {
    this.#findSiblingImage();

    if (this.imageBlock) {
      this.#attachEventListeners();
    }
  }

  /**
   * Finds the sibling image block within the same group.
   */
  #findSiblingImage() {
    const parentGroup = this.closest('.group-block-content');
    if (!parentGroup) return;

    // First try custom element (theme blocks use <image-block>)
    this.imageBlock = parentGroup.querySelector('image-block');

    // If not found, try regular Image block (has .image-block class)
    if (!this.imageBlock) {
      this.imageBlock = parentGroup.querySelector('.image-block');
    }

    // Exclude the product-popup-connector itself if it somehow matches
    if (this.imageBlock && this.imageBlock.closest('product-popup-connector')) {
      this.imageBlock = null;
    }
  }

  /**
   * Attaches event listeners based on device type.
   */
  #attachEventListeners() {
    if (!this.imageBlock || !this.popup) return;

    if (this.isMobile) {
      // Mobile: tap to show, tap outside or scroll to hide
      this.imageBlock.addEventListener('click', this.#handleMobileTap.bind(this));
      document.addEventListener('click', this.#handleOutsideClick.bind(this));
      window.addEventListener('scroll', this.#handleScrollHide.bind(this), { passive: true });
    } else {
      // Desktop: hover to show popup at fixed position
      this.imageBlock.addEventListener('mouseenter', this.#handleMouseEnter.bind(this));
      this.imageBlock.addEventListener('mouseleave', this.#handleMouseLeave.bind(this));

      // Keep popup visible when hovering over the popup itself
      this.popup.addEventListener('mouseenter', this.#handlePopupEnter.bind(this));
      this.popup.addEventListener('mouseleave', this.#handlePopupLeave.bind(this));

      // Hide popup on scroll
      window.addEventListener('scroll', this.#handleScrollHide.bind(this), { passive: true });
    }
  }

  /**
   * Handles mouse enter on image.
   * @param {MouseEvent} event
   */
  #handleMouseEnter = (event) => {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    this.#showPopup();
    this.#applyImageDim(true);
  };

  /**
   * Handles mouse leave on image.
   * @param {MouseEvent} event
   */
  #handleMouseLeave = (event) => {
    // Clear any existing timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    // Delay hiding to allow moving to popup
    this.hideTimeout = setTimeout(() => {
      if (!this.isHoveringPopup) {
        this.#hidePopup();
        this.#applyImageDim(false);
      }
      this.hideTimeout = null;
    }, 100);
  };

  /**
   * Handles mouse enter on popup.
   * @param {MouseEvent} event
   */
  #handlePopupEnter = (event) => {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    this.isHoveringPopup = true;
  };

  /**
   * Handles mouse leave on popup.
   * @param {MouseEvent} event
   */
  #handlePopupLeave = (event) => {
    this.isHoveringPopup = false;
    // Clear any existing timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    // Delay hiding to allow moving back to image
    this.hideTimeout = setTimeout(() => {
      this.#hidePopup();
      this.#applyImageDim(false);
      this.hideTimeout = null;
    }, 100);
  };

  /**
   * Handles mobile tap on image.
   * @param {MouseEvent} event
   */
  #handleMobileTap = (event) => {
    event.preventDefault();
    if (!this.isActive) {
      this.#showPopup();
      this.#applyImageDim(true);
    }
  };

  /**
   * Handles clicks outside the popup to close it.
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (this.isActive && !this.imageBlock.contains(event.target) && !this.popup.contains(event.target)) {
      this.#hidePopup();
      this.#applyImageDim(false);
    }
  };

  /**
   * Handles scroll to hide the popup.
   */
  #handleScrollHide = () => {
    if (this.isActive) {
      this.#hidePopup();
      this.#applyImageDim(false);
    }
  };

  /**
   * Shows the popup with animation.
   */
  #showPopup() {
    // Cancel any pending hide animation
    if (this.hideAnimationTimeout) {
      clearTimeout(this.hideAnimationTimeout);
      this.hideAnimationTimeout = null;
    }

    // If already active and visible, just reposition
    if (this.isActive && this.popup.dataset.visible !== undefined) {
      this.#setPopupPosition();
      return;
    }

    this.isActive = true;
    this.dataset.active = '';
    requestAnimationFrame(() => {
      this.popup.dataset.visible = '';
      // Position after popup is visible for accurate measurements
      requestAnimationFrame(() => {
        this.#setPopupPosition();
      });
    });
  }

  /**
   * Hides the popup with animation.
   */
  #hidePopup() {
    this.isActive = false;
    delete this.popup.dataset.visible;

    // Clear any existing hide animation timeout
    if (this.hideAnimationTimeout) {
      clearTimeout(this.hideAnimationTimeout);
    }

    this.hideAnimationTimeout = setTimeout(() => {
      delete this.dataset.active;
      this.hideAnimationTimeout = null;
    }, 250);
  }

  /**
   * Sets the popup position relative to the image.
   */
  #setPopupPosition() {
    const popupRect = this.popup.getBoundingClientRect();
    const imageRect = this.imageBlock.getBoundingClientRect();

    if (this.isMobile) {
      // Mobile: use mobile offsets
      this.#calculateAndSetPopupPosition(this.offsetXMobile, this.offsetYMobile, popupRect, imageRect);
    } else {
      // Desktop: use desktop offsets
      this.#calculateAndSetPopupPosition(this.offsetX, this.offsetY, popupRect, imageRect);
    }
  }

  /**
   * Helper to calculate and set popup position given offset values.
   * @param {number} offsetX
   * @param {number} offsetY
   * @param {DOMRect} popupRect
   * @param {DOMRect} imageRect
   */
  #calculateAndSetPopupPosition(offsetX, offsetY, popupRect, imageRect) {
    const offsetXpx = (offsetX / 100) * imageRect.width;
    const offsetYpx = (offsetY / 100) * imageRect.height;

    // Calculate popup position relative to image (centered at the offset point)
    const x = imageRect.left + offsetXpx - popupRect.width / 2;
    const y = imageRect.top + offsetYpx - popupRect.height / 2;

    // Prevent popup from going off-screen
    const maxX = window.innerWidth - popupRect.width - 20;
    const maxY = window.innerHeight - popupRect.height - 20;

    const finalX = Math.max(20, Math.min(x, maxX));
    const finalY = Math.max(20, Math.min(y, maxY));

    this.popup.style.left = `${finalX}px`;
    this.popup.style.top = `${finalY}px`;
  }

  /**
   * Applies or removes dim effect on the image.
   * @param {boolean} shouldDim - Whether to dim the image.
   */
  #applyImageDim(shouldDim) {
    if (!this.imageBlock) return;

    if (shouldDim) {
      this.imageBlock.style.filter = `brightness(${100 - this.dimAmount}%)`;
      this.imageBlock.style.transition = 'filter 0.2s ease';
    } else {
      this.imageBlock.style.filter = '';
    }
  }

  disconnectedCallback() {
    if (this.imageBlock) {
      this.imageBlock.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.imageBlock.removeEventListener('mouseleave', this.#handleMouseLeave);
      this.imageBlock.removeEventListener('click', this.#handleMobileTap);
    }
    if (this.popup) {
      this.popup.removeEventListener('mouseenter', this.#handlePopupEnter);
      this.popup.removeEventListener('mouseleave', this.#handlePopupLeave);
    }
    document.removeEventListener('click', this.#handleOutsideClick);
    window.removeEventListener('scroll', this.#handleScrollHide);
  }
  }

  customElements.define('product-popup-connector', ProductPopupConnector);
}
